import { useState, type ReactNode } from 'react';
import {
  AuditEventRowView,
  auditPayloadIsRenderable,
  type AuditEventRow,
} from './AuditEventRowView';

// Public API unchanged from the pre-extract version. The per-row DOM
// and helper formatters now live in AuditEventRowView so the audit PDF
// document can share them. This component adds the web-only concerns:
//   - the outer <ol> with Tailwind utilities
//   - per-row useState disclosure for the payload <pre>
//
// `AuditEventRow` is re-exported so the existing import sites
// (`import { AuditEventList, type AuditEventRow }`) keep working.

export type { AuditEventRow };

type Props = {
  rows: AuditEventRow[];
  // Optional empty-state copy — defaults to "No events yet."
  emptyMessage?: ReactNode;
  // Optional event_type -> human label map. Falls back to the raw
  // dotted event_type ("pharmacy_access.granted") when unmapped.
  eventLabel?: (eventType: string) => string;
};

export function AuditEventList({
  rows,
  emptyMessage = 'No events yet.',
  eventLabel,
}: Props): JSX.Element {
  if (rows.length === 0) {
    return <p className="text-sm text-ink-2">{emptyMessage}</p>;
  }
  return (
    <ol className="audit-list space-y-3 text-sm">
      {rows.map((row) => (
        <AuditEventEntry key={row.id} row={row} eventLabel={eventLabel} />
      ))}
    </ol>
  );
}

function AuditEventEntry({
  row,
  eventLabel,
}: {
  row: AuditEventRow;
  eventLabel: ((eventType: string) => string) | undefined;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const heading = eventLabel ? eventLabel(row.event_type) : row.event_type;
  const hasPayload = auditPayloadIsRenderable(row);
  const trailing = hasPayload ? (
    <button
      type="button"
      aria-expanded={open}
      className="audit-row-toggle text-xs font-medium text-ink underline-offset-4 hover:underline"
      onClick={() => setOpen((v) => !v)}
    >
      {open ? 'Hide' : 'Show'} payload
    </button>
  ) : null;
  return (
    <AuditEventRowView
      row={row}
      heading={heading}
      payloadOpen={open}
      trailing={trailing}
    />
  );
}
